/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-constant-binary-expression */
import React, { useState } from "react";
import { IonButton, IonSpinner } from "@ionic/react";
import { EventRegister } from "react-native-event-listeners";
import { useHistory } from "react-router";
import { useRecoilValueLoadable } from "recoil";
import { IEvidence, IUser } from "@interfaces/Interfaces";
import { evidenceAtom, userAtom } from "@state/State";
import _ from "lodash";
import { differenceInDays, format } from "date-fns";
import DataController from "@controllers/DataController";
import { saveAs } from "file-saver";
import { Directory } from "@capacitor/filesystem";
import WriteBlob from "capacitor-blob-writer";
import { FileOpener } from "@capacitor-community/file-opener";
import { ExportButton } from "parafolio-components";
import * as EvidenceUtils from "@utils/evidenceUtils";
import RemoteDataController from "@controllers/RemoteDataController";
import { initialFilenameSelector } from "@state/DownloadState";
import { PF_CONFIG_SERVER_URL } from "../../Constants";

type Props = {
  helpButtonClicked?: () => void;
};

const AuditProfileCard: React.FC<Props> = (props: Props) => {
  const history = useHistory();

  const user = useRecoilValueLoadable<IUser | null>(userAtom);
  const userName = useRecoilValueLoadable<string>(initialFilenameSelector);

  const evidence = useRecoilValueLoadable<IEvidence[] | null>(evidenceAtom);

  const [exporting, setExporting] = useState<boolean>(false);

  const summaryOfWorkClicked = (): void => {
    history.push("profile/summary-of-work");
  };

  const personalStatementClicked = (): void => {
    history.push("profile/personal-statement");
  };

  const evidenceClicked = (): void => {
    EventRegister.emit("evidence/filter-hcpc");
  };

  const hasSummaryOfWork = (): boolean => {
    let value = false;

    if (user.state === "hasValue" && user.contents) {
      value = typeof user.contents.summaryOfWork !== "undefined";
    }

    return value;
  };

  const hasPersonalStatement = (): boolean => {
    let value = false;

    if (user.state === "hasValue" && user.contents) {
      value = typeof user.contents.personalStatement !== "undefined";
    }

    return value;
  };

  const hasEvidenceWithHCPC = (): boolean => {
    const _evidence: IEvidence[] = _.cloneDeep(evidence.contents) || [];
    const HCPC = _evidence.filter((item: IEvidence) => {
      const evidenceJSON = item.evidenceJSON;
      const onHoldReset = evidenceJSON?.OnHoldReset ?? 0;

      return item.addToHCPCAudit && onHoldReset !== 1 && item.draft !== true;
    });

    return HCPC.length > 0;
  };

  const getEvidenceWithHCPC = (): IEvidence[] => {
    const _evidence: IEvidence[] = _.cloneDeep(evidence.contents) || [];
    const HCPC = _evidence.filter((item: IEvidence) => {
      const evidenceJSON = item.evidenceJSON;
      const onHoldReset = evidenceJSON?.OnHoldReset ?? 0;

      return item.addToHCPCAudit && onHoldReset !== 1 && item.draft !== true;
    });

    return HCPC;
  };

  const getHCPCCount = (): number => {
    const _evidence: IEvidence[] = _.cloneDeep(evidence.contents) || [];
    const HCPC = _evidence.filter((item: IEvidence) => {
      const evidenceJSON = item.evidenceJSON;
      const onHoldReset = evidenceJSON?.OnHoldReset ?? 0;

      return item.addToHCPCAudit && onHoldReset !== 1 && item.draft !== true;
    });

    return HCPC.length;
  };

  const getLastEditedSummaryOfWork = (): string => {
    let value = "";

    if (user.state === "hasValue" && user.contents && user.contents.summaryOfWorkTimestamp) {
      const date = new Date(user.contents.summaryOfWorkTimestamp) || new Date();
      // date = new Date(date.getTime() + (date.getTimezoneOffset() * 60000));
      const dateText = format(date, "d MMM yyyy");
      const timeText = format(date, "h:mmaaa");

      value = `Last edited ${dateText} at ${timeText} · `;
    }

    return value;
  };

  const getLastEditedPersonalStatement = (): string => {
    let value = "";

    if (user.state === "hasValue" && user.contents && user.contents.personalStatementTimestamp) {
      const date = new Date(user.contents.personalStatementTimestamp) || new Date();

      const dateText = format(date, "d MMM yyyy");
      const timeText = format(date, "h:mmaaa");

      value = `Last edited ${dateText} at ${timeText} · `;
    }

    return value;
  };

  const downloadExport = async (): Promise<void> => {
    const evidenceIds = HCPCEvidence.map((item) => item.id);

    const url = PF_CONFIG_SERVER_URL + "/pdf/generate-audit-profile";

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Cache-Control": "no-cache",
        },
        body: JSON.stringify({
          contactID: `${user.contents?.userData?.contactID}`,
          token: user.contents?.token,
          evidenceIds,
        }),
      });

      if (response.status === 200) {
        const blob = await response.blob();
        const filename = `${userName.contents ?? "Untitled"}-Portfolio-${format(new Date(), "dd-MM-yyyy")}`;

        if (await DataController.isWebAsync()) {
          saveAs(blob, filename.trim().length > 0 ? filename.trim() : "Untitled");
        } else {
          const filePath = await WriteBlob({
            path: `ParaFolio/Exports/AuditProfile/${filename.trim().length > 0 ? filename.trim() : "Untitled"}.zip`,
            blob,
            directory: Directory.Library,
            recursive: true,
          });

          await FileOpener.open({
            filePath,
            contentType: "application/zip",
            openWithDefault: false,
          });
        }
      } else {
        window.alert("Failed to download audit profile");
      }

      setExporting(false);
    } catch (error: any) {
      console.log(error);
      RemoteDataController.logError(error);
      setExporting(false);
    }
  };
  const downloadAuditProfile = async (): Promise<void> => {
    setExporting(true);

    try {
      await downloadExport();
    } catch (error) {
      console.error(error);
    }
  };

  const hasEvidenceGap = (): boolean => {
    if (!HCPCEvidence || HCPCEvidence.length === 0) {
      return false;
    }

    const dates = HCPCEvidence.map((item) => new Date(item.date));

    if (dates.length > 1) {
      dates.sort((a, b) => a.getTime() - b.getTime());

      for (let i = 1; i < dates.length; i++) {
        const prevDate = dates[i - 1];
        const currDate = dates[i];

        const diffInDays = differenceInDays(currDate, prevDate);

        if (diffInDays > 90) {
          return true;
        }
      }
    }

    return false;
  };

  const hasLackOfType = (): boolean => {
    if (!HCPCEvidence || HCPCEvidence.length === 0) {
      return false;
    }

    const activityTypes = HCPCEvidence.filter((item) => item.activity?.type !== undefined).map(
      (item) => item.activity!.type
    );

    return Array.from(new Set(activityTypes)).length < 2;
  };

  const summaryWordCount = hasSummaryOfWork()
    ? DataController.getWordCount(user.state === "hasValue" ? user.contents?.summaryOfWork || "" : "")
    : 0;
  const personalStatementWordCount = hasPersonalStatement()
    ? DataController.getWordCount(user.state === "hasValue" ? user.contents?.personalStatement || "" : "")
    : 0;

  const HCPCCount = getHCPCCount();
  const HCPCEvidence = getEvidenceWithHCPC();
  const activityTypesCount = EvidenceUtils.getActivityTypesCountFromEvidence(HCPCEvidence);

  const exportButtonVisible = summaryWordCount > 0 || personalStatementWordCount > 0 || HCPCCount > 0;

  return (
    <div className="HCPCProfileCard">
      <div className="HCPCProfileCardTitleContainer shadow-none">
        <div className="HCPCProfileCardTitle">{"Your Audit Profile"}</div>
        <div className="HCPCProfileCardSubtitle">{"Manage your CPD for your upcoming audits"}</div>
      </div>
      <div className="flex flex-col">
        <div className="HCPCSectionContainer HCPCSectionContainerFilled">
          <div className="HCPCSectionTitle">{"Help"}</div>
          <div className="HCPCWordCount">{"Get help understanding your audit submission"}</div>
          <IonButton
            mode="ios"
            onClick={() => props.helpButtonClicked?.()}
            className="HCPCSectionEditButton my-auto"
          >
            {"View"}
          </IonButton>
        </div>
        <div className="mx-4 h-[1px] bg-grey-40" />
      </div>
      <div className="flex flex-col">
        <div className={`HCPCSectionContainer ${hasSummaryOfWork() ? "HCPCSectionContainerFilled" : ""}`}>
          <div className="HCPCSectionTitle">{"Summary of work"}</div>
          {hasSummaryOfWork() ? (
            <div className="HCPCWordCount">
              {`${getLastEditedSummaryOfWork()} ${summaryWordCount} word${summaryWordCount === 1 ? "" : "s"}`}
            </div>
          ) : (
            <div className="HCPCSectionDescription">
              {
                "A description of your role and the work you do. It's recommended that you write up to 500 words on your main responsibilities, the specialist areas you work in and the people you communicate and work with most."
              }
            </div>
          )}
          {hasSummaryOfWork() ? (
            <IonButton
              mode="ios"
              onClick={() => summaryOfWorkClicked()}
              className="HCPCSectionEditButton my-auto"
            >
              {"Edit"}
            </IonButton>
          ) : (
            <IonButton
              mode="ios"
              onClick={() => summaryOfWorkClicked()}
              className="HCPCSectionGetStartedButton"
            >
              {"Get started"}
            </IonButton>
          )}
        </div>
        <div className="mx-4 h-[1px] bg-grey-40" />
      </div>
      <div className="flex flex-col">
        <div className={`HCPCSectionContainer ${hasPersonalStatement() ? "HCPCSectionContainerFilled" : ""}`}>
          <div className="HCPCSectionTitle">{"Personal statement"}</div>
          {hasPersonalStatement() ? (
            <div className="HCPCWordCount">
              {`${getLastEditedPersonalStatement()} ${personalStatementWordCount} word${personalStatementWordCount === 1 ? "" : "s"}`}
            </div>
          ) : (
            <div className="HCPCSectionDescription">
              {"Use up to 1500 words to focus on your professional development."}
            </div>
          )}
          {hasPersonalStatement() ? (
            <IonButton
              mode="ios"
              onClick={() => personalStatementClicked()}
              className="HCPCSectionEditButton my-auto"
            >
              {"Edit"}
            </IonButton>
          ) : (
            <IonButton
              mode="ios"
              onClick={() => personalStatementClicked()}
              className="HCPCSectionGetStartedButton"
            >
              {"Get started"}
            </IonButton>
          )}
        </div>
        <div className="mx-4 h-[1px] bg-grey-40" />
      </div>
      <div className={`HCPCSectionContainer ${hasEvidenceWithHCPC() ? "HCPCSectionContainerFilledEvidence" : ""}`}>
        <div className="HCPCSectionTitle">{"Evidence"}</div>
        {hasEvidenceWithHCPC() ? (
          <>
            <div className="HCPCWordCount mb-2">{`${HCPCCount} evidence${HCPCCount === 1 ? "" : "s"} tagged for audit${activityTypesCount > 0 ? `, ${activityTypesCount} activity type${activityTypesCount === 1 ? "" : "s"}` : ""}`}</div>
            {hasEvidenceGap() && (
              <div className="text-acc-12px font-acc-500 text-[#F6AD55]">{"You have a gap larger than 90 days"}</div>
            )}
            {hasLackOfType() && (
              <div className="text-acc-12px font-acc-500 text-[#F6AD55]">{"You have fewer than 2 activity types"}</div>
            )}
          </>
        ) : (
          <div className="HCPCSectionDescription">
            {"Use the "}
            <b>{"Add to audit"}</b>
            {" button on a piece of evidence to add it to your Audit Profile. "}
          </div>
        )}
        {hasEvidenceWithHCPC() ? (
          <IonButton
            mode="ios"
            onClick={() => evidenceClicked()}
            className="HCPCSectionEditButton HCPCSectionViewButton my-auto"
          >
            {"View"}
          </IonButton>
        ) : (
          <IonButton
            mode="ios"
            routerLink={"/evidence"}
            className="HCPCSectionGetStartedButton"
          >
            {"Get started"}
          </IonButton>
        )}
      </div>
      {exportButtonVisible ? (
        <div className="mt-3 flex flex-col items-center px-4 pb-5">
          <ExportButton
            disabled={evidence.contents?.length === 0 || exporting}
            onClick={() => downloadAuditProfile()}
          >
            {exporting ? <IonSpinner className="size-6 text-white" /> : <div>{"Download"}</div>}
          </ExportButton>
        </div>
      ) : (
        <div className="flex flex-col">
          <div className="mx-4 h-[1px] bg-grey-40" />
          <div className="HCPCSectionContainer">
            <div className="HCPCSectionTitle">{"Summary of evidence"}</div>
            <div className="HCPCSectionDescription">
              {
                "When adding your evidence as part of your Audit Profile, add a brief description so you can provide a summary list of your CPD evidence"
              }
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditProfileCard;
